import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import Layout from "../../../components/layout/Layout";
import { dataMappingApi, saveFile } from "../../../api/adminToolsApi";
import {
  useCreateMapping,
  useDeleteMapping,
  useEffectiveMapping,
  useMappingModules,
  useMappings,
  useUpdateMapping,
} from "../../../api/queries/adminToolsQueries";
import useCurrentUser from "../../../hooks/useCurrentUser";
import usePermissions from "../../../hooks/usePermissions";
import { getApiErrorMessage } from "../../../utils/apiError";
import { formatDateTime } from "../../../utils/dateTime";
import ImportTabs from "./ImportTabs";
import "../admin.css";
import { useCanModify } from "../../../hooks/usePermissions";

const EMPTY = [];
const clone = (v) => JSON.parse(JSON.stringify(v ?? null));
/** The config inside a mapping / effective-mapping response. */
const configOf = (m) => (m?.configFile ? m.configFile : m?.modules ? m : null);
const messagesOf = (err, fallback) => {
  const m = err?.response?.data?.message;
  return Array.isArray(m) ? m : [getApiErrorMessage(err, fallback)];
};

/** Stored values an enum field may take (from the built-in map's values). */
const storedValuesOf = (builtInField, field) => [
  ...new Set(
    [
      ...Object.values(builtInField?.enum || {}),
      ...Object.values(field.enum || {}),
    ]
      .filter((v) => v !== "" && v !== null && v !== undefined)
      .map(String),
  ),
];

/** fields[].enum = { "file value": "stored value" } for one enum field. */
const ValueMapEditor = ({ field, storedValues, onChange }) => {
  const entries = Object.entries(field.enum || {});
  const set = (list) =>
    onChange(Object.fromEntries(list.filter(([k]) => k !== "")));
  return (
    <div className="map-valuemap">
      {entries.map(([from, to], i) => (
        <div key={i} className="map-valuemap-row">
          <input
            className="form-control form-control-sm"
            value={from}
            onChange={(e) =>
              set(entries.map((x, j) => (j === i ? [e.target.value, x[1]] : x)))
            }
            aria-label="Value in the file"
          />
          <i className="bi bi-arrow-right text-slate-400"></i>
          {storedValues.length ? (
            <select
              className="form-select form-select-sm"
              value={String(to)}
              onChange={(e) =>
                set(
                  entries.map((x, j) => (j === i ? [x[0], e.target.value] : x)),
                )
              }
              aria-label="Stored value"
            >
              {storedValues.map((v) => (
                <option key={v} value={v}>
                  {v}
                </option>
              ))}
            </select>
          ) : (
            <input
              className="form-control form-control-sm"
              value={to ?? ""}
              onChange={(e) =>
                set(
                  entries.map((x, j) => (j === i ? [x[0], e.target.value] : x)),
                )
              }
              aria-label="Stored value"
            />
          )}
          <button
            type="button"
            className="btn btn-sm btn-light"
            onClick={() => set(entries.filter((_, j) => j !== i))}
            aria-label="Remove value"
          >
            <i className="bi bi-x"></i>
          </button>
        </div>
      ))}
      <button
        type="button"
        className="btn btn-link btn-sm p-0 text-start"
        onClick={() =>
          onChange({
            ...(field.enum || {}),
            [`value ${entries.length + 1}`]: storedValues[0] || "",
          })
        }
      >
        + Add value
      </button>
    </div>
  );
};

/** One section (sheet): sheet name + fields table. */
const SectionEditor = ({ section, builtIn, onChange }) => {
  const setField = (target, changes) =>
    onChange({
      ...section,
      fields: section.fields.map((f) =>
        f.target === target ? { ...f, ...changes } : f,
      ),
    });
  const present = new Set(section.fields.map((f) => f.target));
  const missing = (builtIn?.fields || EMPTY).filter(
    (f) => !present.has(f.target),
  );
  const builtInByTarget = new Map(
    (builtIn?.fields || EMPTY).map((f) => [f.target, f]),
  );
  const builtInRequired = new Set(
    (builtIn?.fields || EMPTY).filter((f) => f.required).map((f) => f.target),
  );

  return (
    <>
      <div className="row g-2 mb-3">
        <div className="col-sm-5">
          <label htmlFor="map-sheet" className="profile-label">
            Sheet name
          </label>
          <input
            id="map-sheet"
            className="form-control"
            value={section.sheet || ""}
            onChange={(e) => onChange({ ...section, sheet: e.target.value })}
          />
        </div>
        <div className="col-sm-7 d-flex align-items-end fs-075 text-slate-500">
          Saves to{" "}
          <code className="ms-1">{section.table || section.module}</code>
          {section.primary && <span className="ms-2">· primary sheet</span>}
          {section.externalKey && (
            <span className="ms-2">
              · rows linked by <strong>{section.externalKey}</strong>
            </span>
          )}
        </div>
      </div>
      <div className="table-responsive">
        <table className="table table-sm mb-0 map-fields">
          <thead>
            <tr>
              <th>Column header in the file</th>
              <th>Saves to</th>
              <th>Type</th>
              <th className="text-center">Required</th>
              <th>Default value</th>
              <th>Value map</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {section.fields.map((f) => {
              const lockedRequired = builtInRequired.has(f.target);
              return (
                <tr key={f.target}>
                  <td>
                    <input
                      className="form-control form-control-sm"
                      value={f.source || ""}
                      onChange={(e) =>
                        setField(f.target, { source: e.target.value })
                      }
                      aria-label={`Column for ${f.target}`}
                    />
                  </td>
                  <td className="font-monospace fs-075">{f.target}</td>
                  <td className="fs-075 text-slate-600">
                    {f.type}
                    {f.relation ? ` → ${f.relation}` : ""}
                  </td>
                  <td className="text-center">
                    <input
                      type="checkbox"
                      className="form-check-input"
                      checked={!!f.required}
                      disabled={lockedRequired}
                      onChange={(e) =>
                        setField(f.target, { required: e.target.checked })
                      }
                      aria-label={`${f.target} required`}
                      title={lockedRequired ? "Always required" : ""}
                    />
                  </td>
                  <td>
                    <input
                      className="form-control form-control-sm"
                      value={f.defaultValue ?? ""}
                      onChange={(e) =>
                        setField(f.target, {
                          defaultValue:
                            e.target.value === "" ? undefined : e.target.value,
                        })
                      }
                      aria-label={`Default for ${f.target}`}
                    />
                  </td>
                  <td>
                    {f.type === "enum" ||
                    f.enum ||
                    builtInByTarget.get(f.target)?.enum ? (
                      <ValueMapEditor
                        field={f}
                        storedValues={storedValuesOf(
                          builtInByTarget.get(f.target),
                          f,
                        )}
                        onChange={(map) =>
                          setField(f.target, {
                            enum: Object.keys(map).length ? map : undefined,
                          })
                        }
                      />
                    ) : (
                      <span className="text-slate-400">—</span>
                    )}
                  </td>
                  <td className="text-end">
                    {!f.required && !lockedRequired && (
                      <button
                        type="button"
                        className="btn btn-sm btn-light"
                        onClick={() =>
                          onChange({
                            ...section,
                            fields: section.fields.filter(
                              (x) => x.target !== f.target,
                            ),
                          })
                        }
                        aria-label={`Remove ${f.target}`}
                      >
                        <i className="bi bi-trash"></i>
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {missing.length > 0 && (
        <div className="d-flex align-items-center gap-2 mt-3">
          <span className="fs-075 text-slate-500">Add back:</span>
          <select
            className="form-select form-select-sm w-auto"
            value=""
            onChange={(e) => {
              const f = missing.find((x) => x.target === e.target.value);
              if (f)
                onChange({ ...section, fields: [...section.fields, clone(f)] });
            }}
            aria-label="Add a field"
          >
            <option value="">Choose a field…</option>
            {missing.map((f) => (
              <option key={f.target} value={f.target}>
                {f.source} ({f.target})
              </option>
            ))}
          </select>
        </div>
      )}
    </>
  );
};

/**
 * Admins/HR: how the full employee import reads its workbook: sheet names,
 * column headers, required columns, defaults and value maps per section.
 */
const DataMappingPage = () => {
  const user = useCurrentUser();
  const { superAdmin } = usePermissions();
  const canModify = useCanModify("data-tools", { company: true });
  const companyId = user.companyId || undefined;
  const modulesQuery = useMappingModules();
  const listQuery = useMappings("employee");
  const effective = useEffectiveMapping(companyId);
  const create = useCreateMapping();
  const update = useUpdateMapping();
  const remove = useDeleteMapping();

  const builtIn = useMemo(
    () =>
      (modulesQuery.data || EMPTY).find((m) => m.module === "employee")
        ?.default || null,
    [modulesQuery.data],
  );
  const builtInBySection = useMemo(
    () => new Map((builtIn?.modules || EMPTY).map((s) => [s.module, s])),
    [builtIn],
  );

  const [draft, setDraft] = useState(null); // { id?, name, description, config }
  const [tab, setTab] = useState(0);
  const [errors, setErrors] = useState([]);
  const busy = create.isPending || update.isPending;

  const start = ({ id, name, description, config }) => {
    setDraft({
      id,
      name,
      description: description || "",
      config: clone(config),
    });
    setTab(0);
    setErrors([]);
  };
  const setSection = (i, section) =>
    setDraft((d) => ({
      ...d,
      config: {
        ...d.config,
        modules: d.config.modules.map((s, j) => (j === i ? section : s)),
      },
    }));

  const save = () => {
    if (!draft.name.trim()) return setErrors(["Give the mapping a name."]);
    setErrors([]);
    const body = {
      name: draft.name.trim(),
      description: draft.description.trim() || undefined,
      configFile: draft.config,
    };
    const done = {
      onSuccess: () => {
        toast.success("Mapping saved. Employee imports use it from now on.");
        setDraft(null);
      },
      onError: (err) =>
        setErrors(messagesOf(err, "Could not save the mapping.")),
    };
    if (draft.id) update.mutate({ id: draft.id, ...body }, done);
    else create.mutate({ ...body, module: "employee", companyId }, done);
  };

  const deactivate = (m) => {
    if (
      !window.confirm(
        `Deactivate "${m.name}"? Imports go back to the default mapping.`,
      )
    )
      return;
    remove.mutate(m.id, {
      onSuccess: () => toast.success("Mapping deactivated."),
      onError: (err) =>
        toast.error(getApiErrorMessage(err, "Could not deactivate.")),
    });
  };

  const downloadWorkbook = async () => {
    try {
      saveFile(await dataMappingApi.templateFile(companyId));
    } catch (err) {
      toast.error(getApiErrorMessage(err, "Could not download the workbook."));
    }
  };

  const effConfig = configOf(effective.data);
  const builtInEffective =
    !effective.data?.id || String(effective.data.id).startsWith("default-");
  const effName = builtInEffective
    ? "Built-in default"
    : effective.data?.name || "Company mapping";
  const mappings = listQuery.data || EMPTY;
  const section = draft?.config?.modules?.[tab];

  return (
    <Layout>
      <div className="container-fluid py-4 px-lg-4">
        <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-4">
          <div>
            <Link
              to="/admin"
              className="text-slate-500 fs-080 text-decoration-none"
            >
              <i className="bi bi-arrow-left"></i> Administration
            </Link>
            <h1 className="fw-bold text-dark m-0 page-title">Import</h1>
            <p className="text-muted small mb-0">
              Workbook columns: the sheet and column names the{" "}
              <Link to="/employee/import">new employees workbook</Link> uses.
              Change them only if your workbook uses different names.
            </p>
          </div>
          <button
            type="button"
            className="btn profile-btn profile-btn-white"
            onClick={downloadWorkbook}
          >
            <i className="bi bi-file-earmark-excel"></i> Download blank workbook
          </button>
        </div>

        <ImportTabs />

        {!draft && (
          <>
            <section className="admin-card mb-3">
              <div className="d-flex flex-wrap justify-content-between align-items-center gap-2">
                <div>
                  <div className="approval-meta-label">
                    Imports currently use
                  </div>
                  <div className="fw-semibold">
                    {effective.isLoading ? "…" : effName}
                  </div>
                  {effConfig && (
                    <div className="fs-075 text-slate-500">
                      {(effConfig.modules || EMPTY).length} sheet(s):{" "}
                      {(effConfig.modules || EMPTY)
                        .map((s) => s.sheet)
                        .join(", ")}
                    </div>
                  )}
                </div>
                {canModify && (
                  <div className="d-flex gap-2">
                    {effConfig && (
                      <button
                        type="button"
                        className="btn btn-outline-secondary profile-btn"
                        onClick={() =>
                          start({
                            name: `${effName} (copy)`,
                            config: effConfig,
                          })
                        }
                      >
                        <i className="bi bi-files"></i> Start from current
                      </button>
                    )}
                    <button
                      type="button"
                      className="btn btn-primary profile-btn"
                      onClick={() =>
                        start({ name: "Company mapping", config: builtIn })
                      }
                      disabled={!builtIn}
                    >
                      <i className="bi bi-plus-lg"></i> Copy built-in
                    </button>
                  </div>
                )}
              </div>
            </section>

            <div className="glass-table-card">
              <div className="table-responsive">
                <table className="table table-hover mb-0 admin-table">
                  <thead className="custom-table-header">
                    <tr>
                      <th className="ps-4 py-3">Name</th>
                      <th className="py-3">Company</th>
                      <th className="py-3">Status</th>
                      <th className="py-3">Updated</th>
                      <th className="py-3 pe-4 text-end">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="border-top-0">
                    {mappings.map((m) => {
                      const shared = !m.company;
                      const editable = canModify && (!shared || superAdmin);
                      return (
                        <tr
                          key={m.id}
                          className={
                            m.isActive === false ? "admin-row-inactive" : ""
                          }
                        >
                          <td className="ps-4">
                            <div className="fw-semibold">{m.name}</div>
                            {m.description && (
                              <div className="fs-075 text-slate-500">
                                {m.description}
                              </div>
                            )}
                          </td>
                          <td>
                            {shared ? (
                              <span className="text-slate-500">Shared</span>
                            ) : (
                              m.company?.companyName
                            )}
                          </td>
                          <td>
                            {m.isActive === false ? (
                              <span className="status-pill status-pill--gray">
                                Inactive
                              </span>
                            ) : (
                              <span className="status-pill status-pill--green">
                                Active
                              </span>
                            )}
                            {m.isDefault && (
                              <span className="chip chip--sm ms-1">
                                Default
                              </span>
                            )}
                          </td>
                          <td className="text-nowrap">
                            {formatDateTime(m.updatedAt)}
                          </td>
                          <td className="pe-4 text-end text-nowrap">
                            {editable && (
                              <button
                                type="button"
                                className="btn btn-sm btn-light border att-action-btn"
                                onClick={() =>
                                  start({
                                    id: m.id,
                                    name: m.name,
                                    description: m.description,
                                    config: configOf(m) || builtIn,
                                  })
                                }
                              >
                                <i className="bi bi-pencil"></i> Edit
                              </button>
                            )}
                            {editable && m.isActive !== false && (
                              <button
                                type="button"
                                className="btn btn-sm btn-light border att-action-btn ms-1"
                                onClick={() => deactivate(m)}
                                disabled={remove.isPending}
                              >
                                Deactivate
                              </button>
                            )}
                            {editable && m.isActive === false && (
                              <button
                                type="button"
                                className="btn btn-sm btn-light border att-action-btn ms-1"
                                onClick={() =>
                                  update.mutate(
                                    { id: m.id, isActive: true },
                                    {
                                      onSuccess: () =>
                                        toast.success("Mapping activated."),
                                      onError: (err) =>
                                        toast.error(
                                          getApiErrorMessage(
                                            err,
                                            "Could not activate.",
                                          ),
                                        ),
                                    },
                                  )
                                }
                              >
                                Activate
                              </button>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                    {!listQuery.isLoading && mappings.length === 0 && (
                      <tr>
                        <td colSpan={5} className="approval-empty">
                          No custom mappings yet: imports use the built-in
                          default.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
                {listQuery.isError && (
                  <div className="approval-empty text-danger">
                    {getApiErrorMessage(
                      listQuery.error,
                      "Could not load mappings.",
                    )}
                  </div>
                )}
              </div>
            </div>
          </>
        )}

        {draft && (
          <section className="admin-card">
            {errors.length > 0 && (
              <div className="alert alert-danger fs-085 py-2">
                {errors.length === 1 ? (
                  errors[0]
                ) : (
                  <ul className="mb-0 ps-3">
                    {errors.map((m) => (
                      <li key={m}>{m}</li>
                    ))}
                  </ul>
                )}
              </div>
            )}
            <div className="row g-2 mb-3">
              <div className="col-md-4">
                <label htmlFor="map-name" className="profile-label">
                  Name <span className="text-danger">*</span>
                </label>
                <input
                  id="map-name"
                  className="form-control"
                  value={draft.name}
                  onChange={(e) =>
                    setDraft((d) => ({ ...d, name: e.target.value }))
                  }
                  maxLength={100}
                />
              </div>
              <div className="col-md-8">
                <label htmlFor="map-desc" className="profile-label">
                  Description
                </label>
                <input
                  id="map-desc"
                  className="form-control"
                  value={draft.description}
                  onChange={(e) =>
                    setDraft((d) => ({ ...d, description: e.target.value }))
                  }
                />
              </div>
            </div>
            <div className="map-tabs" role="tablist">
              {(draft.config?.modules || EMPTY).map((s, i) => (
                <button
                  key={s.module}
                  type="button"
                  role="tab"
                  aria-selected={tab === i}
                  className={`map-tab ${tab === i ? "active" : ""}`}
                  onClick={() => setTab(i)}
                >
                  {s.sheet || s.module}
                </button>
              ))}
            </div>
            {section && (
              <SectionEditor
                key={section.module}
                section={section}
                builtIn={builtInBySection.get(section.module)}
                onChange={(s) => setSection(tab, s)}
              />
            )}
            <div className="d-flex justify-content-end gap-2 mt-3">
              <button
                type="button"
                className="btn btn-outline-secondary profile-btn"
                onClick={() => setDraft(null)}
                disabled={busy}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-primary profile-btn"
                onClick={save}
                disabled={busy}
              >
                {busy && (
                  <span
                    className="spinner-border spinner-border-sm"
                    aria-hidden="true"
                  ></span>
                )}
                {draft.id ? "Save changes" : "Save and use"}
              </button>
            </div>
          </section>
        )}
      </div>
    </Layout>
  );
};

export default DataMappingPage;
